/**
 * Multi-file upload component with list display and individual file management.
 * Supports adding multiple files, displaying them in a list, and deleting individual files.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { Upload, Button, Typography, message, Modal, Tooltip, Select, Space, Progress, Tag, Input } from 'antd';
import { UploadOutlined, DeleteOutlined, FileOutlined, PlusOutlined, SelectOutlined, ReloadOutlined, CheckSquareOutlined } from '@ant-design/icons';
import type { RcFile } from 'antd/es/upload';
import { useFileUpload } from '@/hooks/useFileUpload';
import { formatFileSize, formatSpeed } from '@/utils';
import type { UploadConfig, ProjectFile } from '@/types';
import { useUploadProgressStore } from '@/store/uploadProgressStore';
import { abortUpload } from '@/services/obsService';
import { useT } from '@/i18n';

const { Text } = Typography;

interface UploadedFile {
  objectKey: string;
  fileUrl: string;
  fileName: string;
  fileSize: number;
  md5?: string;
}

interface UploadedFileWithType extends UploadedFile {
  fileTypeId: number;
  fileId?: number;
  sourceType?: 'upload' | 'select' | 'git' | 'external';
  remark?: string;  // 文件级备注（选填）
  rawFile?: RcFile;  // present only for deferred uploads (not yet uploaded to OBS)
  uploadKey?: string;  // progress store key, e.g. "package-1-file-23"
  uploadStatus?: 'queued' | 'uploading' | 'done' | 'failed';  // DB status at load time
  file_source?: 'supplier' | 'momenta';  // 透传字段，用于删除时区分来源
}

interface MultiFileUploadProps {
  fileType: string;  // 文件类型标识，用于区分不同类型的文件上传
  onFilesChange: (files: UploadedFileWithType[]) => void;
  existingFiles?: UploadedFileWithType[];
  disabled?: boolean;
  required?: boolean;
  uploadConfig?: UploadConfig;
  maxFiles?: number;
  // 选已有文件功能相关props
  projectFiles?: ProjectFile[];  // 项目已有文件列表
  loadingProjectFiles?: boolean;  // 是否正在加载项目文件
  onLoadProjectFiles?: () => void;  // 加载项目文件的回调
  allowSelectExisting?: boolean;  // 是否允许选择已有文件
  deferUpload?: boolean;  // 延迟上传：选文件后不立即上传，由父组件在提交时上传
  onRetry?: (file: UploadedFileWithType, rawFile: File) => void | Promise<void>;  // 编辑界面重试回调
  onDelete?: (file: UploadedFileWithType) => Promise<void>;  // 编辑界面删除已有文件回调（负责调用后端API）
  deferDelete?: boolean;  // 延迟删除：点击删除只标记，不立即调用后端，由父组件在提交时执行
  onPendingDeleteChange?: (pendingFiles: UploadedFileWithType[]) => void;  // 待删除文件列表变化回调
  showRemark?: boolean;  // 是否显示文件级备注输入框（文档上传区不显示）
}

export const MultiFileUpload: React.FC<MultiFileUploadProps> = ({
  fileType: _fileType,  // 使用 _ 前缀标记为intentionally unused
  onFilesChange,
  existingFiles = [],
  disabled = false,
  required = false,
  uploadConfig,
  maxFiles,
  projectFiles = [],
  loadingProjectFiles = false,
  onLoadProjectFiles,
  allowSelectExisting = false,
  deferUpload = false,
  onRetry,
  onDelete,
  deferDelete = false,
  onPendingDeleteChange,
  showRemark = false,
}) => {
  const { t } = useT();
  const [files, setFiles] = useState<UploadedFileWithType[]>(existingFiles || []);
  const [pendingDeleteIndexes, setPendingDeleteIndexes] = useState<Set<number>>(new Set());
  // In deferDelete mode: replacement files the user uploads after marking an existing file for deletion.
  // Kept separate from `files` so that parent's existingFiles prop doesn't change and the useEffect
  // does not reset pendingDeleteIndexes, preserving the strikethrough on the old file.
  const [pendingNewFiles, setPendingNewFiles] = useState<UploadedFileWithType[]>([]);
  const { isUploading, progress: uploadHookProgress, error, upload, reset } = useFileUpload(uploadConfig);
  const [uploadingFileName, setUploadingFileName] = useState<string>('');
  const [failedFile, setFailedFile] = useState<RcFile | null>(null);
  const [isSelectModalVisible, setIsSelectModalVisible] = useState(false);
  const [selectedFileUrls, setSelectedFileUrls] = useState<string[]>([]);
  const retryInputRefs = useRef<Record<number, HTMLInputElement | null>>({});

  const { progress: storeProgress } = useUploadProgressStore();

  // Sync existingFiles to local state only when content actually changes.
  // Parent components often pass inline array literals that create new references
  // every render — a plain reference comparison would fire setFiles every render
  // and trigger infinite update loops.
  const prevExistingJson = useRef(JSON.stringify(existingFiles || []));
  useEffect(() => {
    const json = JSON.stringify(existingFiles || []);
    if (json === prevExistingJson.current) return;
    prevExistingJson.current = json;

    if (deferDelete && pendingDeleteIndexes.size > 0) {
      // existingFiles changed because we reported a replacement file via onFilesChange.
      // The user still has pending-delete marks — do not reset them or pendingNewFiles.
      return;
    }

    setFiles(existingFiles || []);
    setPendingDeleteIndexes(new Set());
    setPendingNewFiles([]);
  }, [existingFiles, deferDelete, pendingDeleteIndexes]);

  const handleUpload = useCallback(
    async (file: RcFile) => {
      // Check max files limit (in deferDelete mode, pending-delete files free up slots; pendingNewFiles occupy slots)
      const currentEffectiveCount = deferDelete
        ? files.length - pendingDeleteIndexes.size + pendingNewFiles.length
        : files.length;
      if (maxFiles && currentEffectiveCount >= maxFiles) {
        message.warning(t('最多只能上传{max}个文件', { max: maxFiles }));
        return false;
      }

      if (deferUpload) {
        // Deferred mode: just record the raw file, don't upload now
        const pendingFile: UploadedFileWithType = {
          objectKey: '',
          fileUrl: '',
          fileName: file.name,
          fileSize: file.size,
          fileTypeId: 0,
          sourceType: 'upload',
          rawFile: file,
        };

        if (deferDelete && pendingDeleteIndexes.size > 0) {
          // When there are pending-delete marks, keep the new file in pendingNewFiles so
          // that `files` (and thus existingFiles→useEffect) stays unchanged and the
          // strikethrough on the old file is preserved.
          const updatedNewFiles = [...pendingNewFiles, pendingFile];
          setPendingNewFiles(updatedNewFiles);
          // Report effective files: remaining old files (not marked for delete) + new files
          const remainingOldFiles = files.filter((_, i) => !pendingDeleteIndexes.has(i));
          console.log('[MultiFileUpload:handleUpload:deferDelete] adding to pendingNewFiles, reporting effectiveFiles=', [...remainingOldFiles, ...updatedNewFiles].map(f => ({ fileName: f.fileName })));
          onFilesChange([...remainingOldFiles, ...updatedNewFiles]);
        } else {
          const updatedFiles = [...files, pendingFile];
          setFiles(updatedFiles);
          console.log('[MultiFileUpload:handleUpload:deferred] updatedFiles=', updatedFiles.map(f => ({ fileName: f.fileName, fileId: f.fileId })));
          onFilesChange(updatedFiles);
        }
        return false;
      }

      setUploadingFileName(file.name);
      setFailedFile(null); // Clear previous failed file

      try {
        const result = await upload(file);
        const newFile: UploadedFileWithType = {
          ...result,
          fileTypeId: 0, // Will be set by parent component
          sourceType: 'upload',
        };

        // Update state and notify parent
        const updatedFiles = [...files, newFile];

        setFiles(updatedFiles);
        onFilesChange(updatedFiles);
        message.success(t('{name} 上传成功', { name: file.name }));
        reset();
        setUploadingFileName('');
      } catch (err) {
        setFailedFile(file); // Save failed file for retry
        setUploadingFileName('');
        message.error(error || t('{name} 上传失败', { name: file.name }));
      }

      return false; // Prevent default upload behavior
    },
    [files, pendingNewFiles, upload, onFilesChange, error, reset, maxFiles, deferUpload, deferDelete, pendingDeleteIndexes, t]
  );

  const handleRetry = useCallback(() => {
    if (failedFile) {
      reset(); // Clear error state
      handleUpload(failedFile);
    }
  }, [failedFile, reset, handleUpload]);

  const handleRemove = useCallback(
    (index: number) => {
      // In deferDelete mode, indexes >= files.length refer to pendingNewFiles
      if (deferDelete && index >= files.length) {
        const newFileIndex = index - files.length;
        const updatedNewFiles = pendingNewFiles.filter((_, i) => i !== newFileIndex);
        setPendingNewFiles(updatedNewFiles);
        // Re-report effective files: remaining old files (not marked for delete) + remaining new files
        const remainingOldFiles = files.filter((_, i) => !pendingDeleteIndexes.has(i));
        console.log('[MultiFileUpload:handleRemove:pendingNew] removed index=', newFileIndex, 'remaining=', [...remainingOldFiles, ...updatedNewFiles].map(f => ({ fileName: f.fileName })));
        onFilesChange([...remainingOldFiles, ...updatedNewFiles]);
        return;
      }

      const fileToRemove = files[index];

      // Deferred delete mode: just toggle the mark on existing files, no confirmation dialog
      if (deferDelete) {
        setPendingDeleteIndexes(prev => {
          const next = new Set(prev);
          if (next.has(index)) {
            next.delete(index);
            console.log('[MultiFileUpload:handleRemove:deferDelete] UNMARK index=', index, 'file=', { fileName: fileToRemove.fileName, fileId: fileToRemove.fileId }, 'pendingIndexes after=', [...next]);
          } else {
            next.add(index);
            console.log('[MultiFileUpload:handleRemove:deferDelete] MARK index=', index, 'file=', { fileName: fileToRemove.fileName, fileId: fileToRemove.fileId }, 'pendingIndexes after=', [...next]);
          }
          const pendingFiles = files.filter((_, i) => next.has(i));
          onPendingDeleteChange?.(pendingFiles);
          return next;
        });
        return;
      }

      Modal.confirm({
        title: t('确认删除'),
        content: t('确定要删除文件 {name} 吗？', { name: fileToRemove.fileName }),
        okText: t('确认'),
        cancelText: t('取消'),
        okType: 'danger',
        onOk: async () => {
          console.log('[MultiFileUpload:handleRemove] fileToRemove:', {
            fileId: fileToRemove.fileId,
            fileName: fileToRemove.fileName,
            fileUrl: fileToRemove.fileUrl,
            objectKey: fileToRemove.objectKey,
            sourceType: fileToRemove.sourceType,
            file_source: (fileToRemove as any).file_source,
            uploadKey: fileToRemove.uploadKey,
          });
          // 先中止该文件的上传（不影响其他文件）
          if (fileToRemove.uploadKey) {
            abortUpload(fileToRemove.uploadKey);
          }
          // 如果有后端删除回调（编辑模式下已有文件），先调用后端删除
          if (onDelete && (fileToRemove.fileId || fileToRemove.fileUrl)) {
            console.log('[MultiFileUpload:handleRemove] calling onDelete, fileId=', fileToRemove.fileId);
            try {
              await onDelete(fileToRemove);
              console.log('[MultiFileUpload:handleRemove] onDelete success');
            } catch {
              console.error('[MultiFileUpload:handleRemove] onDelete failed');
              message.error(t('删除文件失败'));
              return;
            }
          } else {
            console.log('[MultiFileUpload:handleRemove] skip onDelete: onDelete=', !!onDelete, 'fileId=', fileToRemove.fileId);
          }
          // Clear progress store entry to prevent init useEffect from restoring this file
          if (fileToRemove.uploadKey) {
            useUploadProgressStore.getState().clearProgress(fileToRemove.uploadKey);
          }
          const updatedFiles = files.filter((_, i) => i !== index);
          setFiles(updatedFiles);
          onFilesChange(updatedFiles);
          message.success(t('文件已删除'));
        },
      });
    },
    [files, pendingNewFiles, onFilesChange, onDelete, deferDelete, onPendingDeleteChange, t]
  );

  const handleRemarkChange = useCallback(
    (index: number, remark: string) => {
      // In deferDelete mode, indexes >= files.length refer to pendingNewFiles
      if (deferDelete && index >= files.length) {
        const newFileIndex = index - files.length;
        const updatedNewFiles = pendingNewFiles.map((f, i) =>
          i === newFileIndex ? { ...f, remark } : f
        );
        setPendingNewFiles(updatedNewFiles);
        const remainingOldFiles = files.filter((_, i) => !pendingDeleteIndexes.has(i));
        onFilesChange([...remainingOldFiles, ...updatedNewFiles]);
        return;
      }

      if (pendingDeleteIndexes.has(index)) return;

      const updatedFiles = files.map((f, i) => (i === index ? { ...f, remark } : f));
      setFiles(updatedFiles);
      if (deferDelete) {
        const remainingOldFiles = updatedFiles.filter((_, i) => !pendingDeleteIndexes.has(i));
        onFilesChange([...remainingOldFiles, ...pendingNewFiles]);
      } else {
        onFilesChange(updatedFiles);
      }
    },
    [files, pendingNewFiles, pendingDeleteIndexes, deferDelete, onFilesChange]
  );

  // In deferDelete mode: pending-delete files free up slots; pendingNewFiles occupy slots
  const effectiveFileCount = deferDelete
    ? files.length - pendingDeleteIndexes.size + pendingNewFiles.length
    : files.length;
  const canAddMore = !maxFiles || effectiveFileCount < maxFiles;

  // 打开选择已有文件对话框
  const handleOpenSelectModal = useCallback(() => {
    if (onLoadProjectFiles) {
      onLoadProjectFiles();
    }
    setIsSelectModalVisible(true);
    // 预选当前已选择的文件
    const currentSelectedUrls = files
      .filter(f => f.sourceType === 'select')
      .map(f => f.fileUrl);
    setSelectedFileUrls(currentSelectedUrls);
  }, [onLoadProjectFiles, files]);

  // 确认选择已有文件
  const handleConfirmSelect = useCallback(() => {
    const selectedFiles: UploadedFileWithType[] = selectedFileUrls
      .map(url => {
        const projectFile = projectFiles.find(f => f.file_url === url);
        if (!projectFile) return null;

        const file: UploadedFileWithType = {
          objectKey: projectFile.object_key,
          fileUrl: projectFile.file_url,
          fileName: projectFile.file_name,
          fileSize: projectFile.file_size,
          fileTypeId: 0, // Will be set by parent
          md5: '', // ProjectFile doesn't have checksum, will be empty
          sourceType: 'select',
        };
        return file;
      })
      .filter((f): f is UploadedFileWithType => f !== null);

    if (selectedFiles.length === 0) {
      message.warning(t('请至少选择一个文件'));
      return;
    }

    // deferDelete 且有待删除标记：镜像 handleUpload 行为，保留删除线
    if (deferDelete && pendingDeleteIndexes.size > 0) {
      const newSelectedUrls = new Set(selectedFiles.map(f => f.fileUrl));
      const retainedNewFiles = pendingNewFiles.filter(f => !newSelectedUrls.has(f.fileUrl));
      const updatedNewFiles = [...retainedNewFiles, ...selectedFiles];
      const remainingOldFiles = files.filter((_, i) => !pendingDeleteIndexes.has(i));
      if (maxFiles && remainingOldFiles.length + updatedNewFiles.length > maxFiles) {
        message.warning(t('最多只能选择{max}个文件，已自动截取前{max}个', { max: maxFiles }));
        updatedNewFiles.splice(maxFiles - remainingOldFiles.length);
      }
      setPendingNewFiles(updatedNewFiles);
      onFilesChange([...remainingOldFiles, ...updatedNewFiles]);
      setIsSelectModalVisible(false);
      message.success(t('已选择 {count} 个文件', { count: selectedFiles.length }));
      return;
    }

    // 非 deferDelete / 无待删除：合并逻辑
    // 1. 保留所有已有文件（去除与新选择文件URL重复的文件，避免重复添加）
    // 2. 添加新选择的文件
    // 3. 如果有maxFiles限制，需要检查是否超出
    const newSelectedUrls = new Set(selectedFiles.map(f => f.fileUrl));
    const existingFiles = files.filter(f => !newSelectedUrls.has(f.fileUrl));
    let mergedFiles = [...existingFiles, ...selectedFiles];

    if (maxFiles && mergedFiles.length > maxFiles) {
      message.warning(t('最多只能选择{max}个文件，已自动截取前{max}个', { max: maxFiles }));
      mergedFiles = mergedFiles.slice(0, maxFiles);
    }

    setFiles(mergedFiles);
    onFilesChange(mergedFiles);
    setIsSelectModalVisible(false);
    message.success(t('已选择 {count} 个文件', { count: selectedFiles.length }));
  }, [selectedFileUrls, projectFiles, onFilesChange, maxFiles, files, pendingNewFiles, pendingDeleteIndexes, deferDelete, t]);

  // 渲染单个文件的状态区（大小 / 进度 / 失败重试）
  const renderFileStatus = (file: UploadedFileWithType, index: number) => {
    const isDeferred = !!file.rawFile && !file.uploadKey;

    if (isDeferred) {
      // 本地待提交文件：显示待上传标签，不显示大小
      return (
        <Text type="warning" style={{ fontSize: 12 }}>{t('提交后上传')}</Text>
      );
    }

    const storeEntry = file.uploadKey ? storeProgress[file.uploadKey] : undefined;
    const effectiveStatus = storeEntry?.status ?? file.uploadStatus;
    const isDone = !effectiveStatus || effectiveStatus === 'done';

    if (isDone) {
      return (
        <Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
          {formatFileSize(file.fileSize)}
        </Text>
      );
    }

    if (effectiveStatus === 'uploading' || effectiveStatus === 'queued') {
      return (
        <Space orientation="vertical" size={2} style={{ width: '100%' }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {effectiveStatus === 'queued' ? t('排队中') : t('上传中')}
          </Text>
          {storeEntry && (
            <Progress percent={storeEntry.percent} size="small" status="active" style={{ marginBottom: 0 }} />
          )}
          {storeEntry?.currentSpeed !== undefined && storeEntry.currentSpeed > 0 && (
            <Text type="secondary" style={{ fontSize: 12 }}>{formatSpeed(storeEntry.currentSpeed)}</Text>
          )}
        </Space>
      );
    }

    if (effectiveStatus === 'failed') {
      const savedFile = storeEntry?.rawFile;
      return (
        <Space size={4}>
          <Tag color="error" style={{ margin: 0 }}>{t('上传失败')}</Tag>
          {savedFile ? (
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={() => onRetry?.(file, savedFile)}
            >
              {t('重试')}
            </Button>
          ) : (
            <>
              <input
                type="file"
                style={{ display: 'none' }}
                ref={el => { retryInputRefs.current[index] = el; }}
                onChange={e => {
                  const f = e.target.files?.[0];
                  if (f) onRetry?.(file, f);
                  e.target.value = '';
                }}
              />
              <Button
                size="small"
                icon={<ReloadOutlined />}
                onClick={() => retryInputRefs.current[index]?.click()}
              >
                {t('选择文件重试')}
              </Button>
            </>
          )}
        </Space>
      );
    }

    // fallback: show size
    return (
      <Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
        {formatFileSize(file.fileSize)}
      </Text>
    );
  };

  return (
    <div>
      {/* File list: existing files first, then replacement files pending upload */}
      {(files.length > 0 || pendingNewFiles.length > 0) && (
        <div style={{ marginBottom: 16, border: '1px solid #d9d9d9', borderRadius: 6 }}>
          {[...files, ...pendingNewFiles].map((file, index) => {
            const isPendingDelete = pendingDeleteIndexes.has(index);
            return (
            <div
              key={index}
              className="flex items-start justify-between py-2 border-b border-gray-100 last:border-0"
              style={{ padding: '8px 12px', opacity: isPendingDelete ? 0.5 : 1 }}
            >
              <div style={{ display: 'flex', gap: '12px', flex: 1, overflow: 'hidden' }}>
                {/* 文件图标 - 缩小 */}
                <div style={{ flexShrink: 0 }}>
                  <FileOutlined style={{ fontSize: 16, marginTop: '2px', color: '#8c8c8c' }} />
                </div>

                {/* 文件信息 */}
                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {/* 第1行：文件名 */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Tooltip title={file.fileName}>
                      <Text
                        style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          textDecoration: isPendingDelete ? 'line-through' : undefined,
                          color: isPendingDelete ? '#ff4d4f' : undefined,
                        }}
                      >
                        {file.fileName}
                      </Text>
                    </Tooltip>
                    {file.sourceType === 'select' && (
                      <Text type="secondary" style={{ fontSize: 12, flexShrink: 0 }}>
                        {t('(选已有)')}
                      </Text>
                    )}
                    {isPendingDelete && (
                      <Text type="danger" style={{ fontSize: 12, flexShrink: 0 }}>
                        {t('(待删除)')}
                      </Text>
                    )}
                  </div>

                  {/* 第2行：文件大小 / 上传状态 */}
                  <div>
                    {renderFileStatus(file, index)}
                  </div>

                  {/* 第3行：MD5值 */}
                  {file.md5 && (
                    <Tooltip title={t('完整MD5: {md5}', { md5: file.md5 })}>
                      <div>
                        <Text
                          type="secondary"
                          style={{
                            fontSize: 12,
                            cursor: 'help',
                            display: 'inline-block',
                            maxWidth: '100%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          MD5: {file.md5}
                        </Text>
                      </div>
                    </Tooltip>
                  )}

                  {/* 第4行：备注（选填） */}
                  {showRemark && (
                    <Input
                      size="small"
                      placeholder={t('备注（选填）')}
                      value={file.remark ?? ''}
                      maxLength={500}
                      allowClear
                      disabled={disabled || isPendingDelete}
                      onChange={(e) => handleRemarkChange(index, e.target.value)}
                    />
                  )}
                </div>
              </div>
              <div className="flex gap-2" style={{ flexShrink: 0, marginLeft: 8 }}>
                <Tooltip title={
                  !deferDelete ? t('删除文件') :
                  isPendingDelete ? t('取消删除标记') : t('标记删除（更新后生效）')
                }>
                  <Button
                    type="link"
                    danger
                    size="small"
                    icon={isPendingDelete ? <CheckSquareOutlined /> : <DeleteOutlined />}
                    onClick={() => handleRemove(index)}
                    disabled={disabled}
                  />
                </Tooltip>
              </div>
            </div>
            );
          })}
        </div>
      )}

      {/* Upload and Select buttons */}
      <Space>
        {/* 上传按钮：当未达到maxFiles限制时显示 */}
        {canAddMore && (
          <Upload
            beforeUpload={handleUpload}
            showUploadList={false}
            disabled={disabled || isUploading}
          >
            <Button
              icon={effectiveFileCount === 0 ? <UploadOutlined /> : <PlusOutlined />}
              disabled={disabled || isUploading}
              loading={isUploading}
            >
              {effectiveFileCount === 0 ? t('上传文件') : t('上传更多文件')}
            </Button>
          </Upload>
        )}

        {/* 选择已有按钮：当未达到maxFiles限制时显示（如果启用） */}
        {allowSelectExisting && canAddMore && (
          <Button
            icon={<SelectOutlined />}
            disabled={disabled || isUploading}
            onClick={handleOpenSelectModal}
          >
            {t('选择已有')}
          </Button>
        )}
      </Space>

      {/* Upload progress */}
      {isUploading && (
        <div style={{ marginTop: 8 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t('正在上传 {name}... {percent}%', { name: uploadingFileName, percent: uploadHookProgress?.percent || 0 })}
            {uploadHookProgress?.currentSpeed ? ` (${formatSpeed(uploadHookProgress.currentSpeed)})` : ''}
          </Text>
        </div>
      )}

      {/* Upload error with retry */}
      {error && failedFile && !isUploading && (
        <div style={{ marginTop: 8 }}>
          <Space>
            <Text type="danger" style={{ fontSize: 12 }}>
              {t('{name} 上传失败: {error}', { name: failedFile.name, error })}
            </Text>
            <Button type="link" size="small" onClick={handleRetry} disabled={disabled}>
              {t('重试')}
            </Button>
          </Space>
        </div>
      )}

      {/* Max files hint */}
      {maxFiles && (
        <div style={{ marginTop: 8 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t('已上传 {count}/{max} 个文件', { count: effectiveFileCount, max: maxFiles })}
          </Text>
        </div>
      )}

      {/* Required hint */}
      {required && files.length === 0 && pendingNewFiles.length === 0 && (
        <div style={{ marginTop: 8 }}>
          <Text type="danger" style={{ fontSize: 12 }}>
            {t('* 请至少上传一个文件')}
          </Text>
        </div>
      )}

      {/* 选择已有文件Modal */}
      <Modal
        title={t('选择已有文件')}
        open={isSelectModalVisible}
        onCancel={() => setIsSelectModalVisible(false)}
        onOk={handleConfirmSelect}
        width={800}
        okText={t('确认选择')}
        cancelText={t('取消')}
      >
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary">
            {maxFiles === 1 ? t('从项目已有文件中选择一个文件') : t('从项目已有文件中选择（可多选）')}
          </Text>
        </div>
        <Select
          mode={maxFiles === 1 ? undefined : 'multiple'}
          placeholder={maxFiles === 1 ? t('请选择文件') : t('请选择文件（可多选）')}
          style={{ width: '100%' }}
          value={maxFiles === 1 ? selectedFileUrls[0] : selectedFileUrls}
          onChange={(value) => {
            if (maxFiles === 1) {
              setSelectedFileUrls(value ? [value as string] : []);
            } else {
              setSelectedFileUrls(value as string[]);
            }
          }}
          loading={loadingProjectFiles}
          optionFilterProp="children"
          showSearch
          maxTagCount="responsive"
        >
          {projectFiles.filter(f => f.file_url).map((file) => (
            <Select.Option key={file.id} value={file.file_url}>
              <Space>
                <FileOutlined />
                <span>{file.file_name}</span>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  ({formatFileSize(file.file_size)})
                </Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  - {file.package_name}
                </Text>
              </Space>
            </Select.Option>
          ))}
        </Select>
        {projectFiles.length === 0 && !loadingProjectFiles && (
          <div style={{ marginTop: 16, textAlign: 'center' }}>
            <Text type="secondary">{t('暂无同类型文件')}</Text>
          </div>
        )}
      </Modal>
    </div>
  );
};
